// @flow
import type { GlobalState, RichModule } from "../../lib/types";
import { getActiveModule, getModuleByType } from "../../store";
import { GalleryItemType, ModuleTypes } from "../../lib/types";
import { getYouTubeID, getYouTubePreviewSrc } from "../../lib/utils";

/**
 * Get the number of the module which was active before user opened the gallery.
 * @param state
 * @returns {?number}
 */
export const getModuleBeforeGallery = (state: GlobalState) => {
  return state.moduleBeforeGallery;
};
/**
 * Return only src's of the gallery items.
 * Depends on the the type/role of the currently active module.
 * May be this should be moved to the Gallery module itself.
 */
export const getGalleryContent = (state: GlobalState, galleryModule: RichModule) => {
  if (galleryModule && (galleryModule.isFloorplan() || galleryModule.isInChatFloorplan())) {
    return getFloorPlans(state);
  }
  if (galleryModule && galleryModule.isVideo()) {
    return getVideos(state);
  }
  if (galleryModule && (galleryModule.isDigitalTour() || galleryModule.isInChatDigitalTour())) {
    return getDigitalTours(state);
  }
  return getImages(state);
};
/**
 * Return the floor plans from the config.
 */
export const getFloorPlans = (state: GlobalState) => {
  return (state.config && state.config.floorplans) || [];
};
/**
 * Return the videos from the config.
 */
export const getVideos = (state: GlobalState) => {
  return (state.config && state.config.videos) || [];
};
/**
 * Return the images from the config.
 */
export const getImages = (state: GlobalState) => {
  const images = (state.config && state.config.images) || [];
  return images.map((src) => src.replace("familyassets.s3.amazonaws.com:443", "d35wouidr0tyww.cloudfront.net"));
};
/**
 * Return the digital tours from the config.
 */
export const getDigitalTours = (state: GlobalState) => {
  return (state.config && state.config.digital_tours) || [];
};
/**
 * Tell if the gallery should be shown.
 * @param state
 * @returns {*|boolean}
 */
export const shouldShowGallery = (state: GlobalState): boolean => {
  const activeModule = getActiveModule(state);
  return activeModule && activeModule.shouldShowGallery();
};

/**
 * Prepare gallery items for the ImageGallery 3rd party component.
 * Depends on the type/role of the currently active module.
 * @returns {*}
 */
export const getGalleryItems = (state: GlobalState, galleryModule: RichModule) => {
  const galleryContent = getGalleryContent(state, galleryModule) || [];
  const images = getImages(state);
  const datePickerModule = getModuleByType(state, ModuleTypes.datePicker);

  let galleryItems;

  if (galleryModule.isVideo() || galleryModule.isDigitalTour() || galleryModule.isInChatDigitalTour()) {
    galleryItems = galleryContent.map((content, index) => {
      if (content.includes("youtube")) {
        return {
          contentType: GalleryItemType.YOUTUBE,
          original: `${getYouTubeID(content)}#${index}`,
          thumbnail: `${getYouTubePreviewSrc(content)}#${index}`,
        };
      }
      return {
        contentType: GalleryItemType.IFRAME,
        original: content,
        thumbnail: null,
      };
    });
  } else {
    galleryItems = galleryContent.map((url) => ({
      original: url,
      thumbnail: url,
    }));
  }
  if (images && datePickerModule) {
    galleryItems.push({
      contentType: "goToTour",
      // `#` in the url here exists to hack the gallery item `keys` - it's rendering misbehaves
      // otherwise when we have duplicated urls.
      original: `${images[0]}#`,
      thumbnail: null,
    });
  }
  return galleryItems;
};
